import React from 'react'
// libs
import styled from 'styled-components'
// components
import { Collapse } from 'components/Modals/PlanItems/styles'
// view components
import Field from 'components/Modals/RiskViewModal/components/Form/components/FieldsGroup/components/Field'
import FieldsGroup from 'components/Modals/RiskViewModal/components/Form/components/FieldsGroup'
// themes
import { colors } from 'themes/index'
// types
import { FieldType, IField, IRiskTemplate } from 'types/graphql'
// utils
import orderBy from 'lodash/orderBy'

const Wrapper = styled.div`
  ${Collapse} {
    padding: 0;
  }
`

const WarningText = styled.div`
  font-size: 14px;
  color: ${colors.gray320};
  padding-top: 12px;
  border-top: 1px solid;
`

const BASIC_FIELDS = [
  { id: 'PARENT', label: 'Parent', position: 0 },
  { id: 'createdAt', label: 'Created At', position: 0 },
  { id: 'createdBy', label: 'Created By', position: 0 },
  { id: 'updatedAt', label: 'Updated At', position: 0 },
  { id: 'updatedBy', label: 'Updated By', position: 0 }
] as IField[]

const FIELD_TYPES_TO_EXCLUDE = [
  FieldType.Label,
  FieldType.RecordLinks,
  FieldType.InjuryMasterLinks,
  FieldType.InjuryMasterParentLinks,
  FieldType.ParentRelatedForms
]

interface Props {
  isRelated?: boolean
  riskTemplate: IRiskTemplate
}

const Fields: React.FC<Props> = ({ isRelated, riskTemplate }) => {
  const [isExpanded, setExpanded] = React.useState(false)

  const fields: IField[] = [
    ...riskTemplate.fields,
    ...riskTemplate.fieldLinks.map((item) => ({
      ...item.field,
      parentFieldId: item.parentFieldId,
      position: item.position,
      label: item.fieldLabel
    }))
  ].filter(({ active, fieldType }) => active && !FIELD_TYPES_TO_EXCLUDE.includes(fieldType))

  const firstLevelFields: IField[] = orderBy(
    (isRelated ? fields : [...BASIC_FIELDS, ...fields]).filter((field) => !field.parentFieldId),
    'position'
  )

  const preparedRiskTemplate = { ...riskTemplate, fields }

  return (
    <Wrapper>
      {isRelated && (
        <WarningText>
          Warning, fields selected from Related Forms will be visible to any user that has access to the main form
        </WarningText>
      )}
      <Collapse isCollapsed={!isExpanded} handleClick={() => setExpanded(!isExpanded)}>
        {isRelated ? `Related Form: ${riskTemplate.name}` : 'Columns'}
      </Collapse>
      {isExpanded &&
        firstLevelFields.map((field) => {
          const isTable = field.fieldType === FieldType.Table
          const isSection = field.fieldType === FieldType.Section

          if (isTable || isSection) {
            const subFields = orderBy(
              fields.filter((item) => item.parentFieldId === field.id),
              'position'
            )

            return (
              <FieldsGroup
                field={field}
                key={field.id}
                fields={subFields}
                parentId={field.id}
                isRelated={isRelated}
                riskTemplate={preparedRiskTemplate}
              />
            )
          }

          return <Field key={field.id} field={field} isRelated={isRelated} riskTemplate={riskTemplate} />
        })}
    </Wrapper>
  )
}

export default Fields
